import { useState, useRef, useEffect, Dispatch, SetStateAction } from 'react'
import {
  Paper,
  List,
  ListItem,
  Typography,
  Stack,
  InputAdornment,
} from '@mui/material'
import { Check } from '@mui/icons-material'
import { StyledTextField } from '~/@ui/form/StyledTextField'

interface Prediction {
  place_id: string
  description: string
  structured_formatting?: {
    main_text: string
    secondary_text: string
  }
  types?: string[]
}

interface CustomAddressAutocompleteProps {
  placeholder?: string
  value?: string
  onChange?: (value: string) => void
  onAddressSelect?: (prediction: Prediction) => void
  predictions?: Prediction[]
  onInputChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  className?: string
  disabled?: boolean
  error: boolean
  setError: Dispatch<SetStateAction<boolean>>
  helperText?: string
  isValidAddress: boolean
  setIsValidAddress: Dispatch<SetStateAction<boolean>>
}

export const CustomAddressAutocomplete = ({
  placeholder = 'Adresa',
  value = '',
  onChange,
  onAddressSelect,
  predictions = [],
  onInputChange,
  className = '',
  disabled = false,
  error = false,
  setError,
  helperText,
  isValidAddress,
  setIsValidAddress,
}: CustomAddressAutocompleteProps) => {
  const [inputValue, setInputValue] = useState(value)
  const [showPredictions, setShowPredictions] = useState(false)
  const [selectedAddress, setSelectedAddress] = useState<string>('')
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setInputValue(value)
  }, [value])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowPredictions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setInputValue(newValue)
    onChange?.(newValue)
    onInputChange?.(e)
    setShowPredictions(newValue.length > 0 && predictions.length > 0)

    // Skontroluj, či zadaná hodnota sa zhoduje s predtým vybranou adresou
    if (newValue === selectedAddress && selectedAddress !== '') {
      setIsValidAddress(true)
    } else {
      setIsValidAddress(false)
    }
  }

  const handlePredictionClick = (prediction: Prediction) => {
    setInputValue(prediction.description)
    setShowPredictions(false)
    setSelectedAddress(prediction.description)
    setIsValidAddress(true)
    onChange?.(prediction.description)
    onAddressSelect?.(prediction)
  }

  const handleInputFocus = () => {
    setError(false)
    setShowPredictions(inputValue.length > 0 && predictions.length > 0)
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <StyledTextField
        placeholder={placeholder}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        disabled={disabled}
        error={error}
        helperText={helperText}
        fullWidth
        InputProps={{
          autoComplete: 'off',
          endAdornment: isValidAddress && (
            <InputAdornment position="end">
              <Check
                sx={{
                  color: '#4CAF50', // zelená farba
                  fontSize: 20,
                }}
              />
            </InputAdornment>
          ),
        }}
      />

      {/* Custom predictions dropdown - styled like StyledSelect */}
      {showPredictions && predictions.length > 0 && (
        <Paper
          className="absolute top-full left-0 right-0 z-50 mt-1"
          sx={{
            backgroundColor: '#F4F4FF',
            boxShadow: 'inset 0 0 0 1px #E0E5EC',
            borderRadius: '20px',
            overflow: 'hidden',
            maxHeight: '240px',
            overflowY: 'auto',
          }}>
          <List className="p-0">
            {predictions.map((prediction, index) => (
              <ListItem
                key={prediction.place_id || index}
                className="cursor-pointer"
                onClick={() => handlePredictionClick(prediction)}
                sx={(theme) => ({
                  height: '56px',
                  justifyContent: 'start',
                  alignItems: 'center',
                  paddingLeft: '15px',
                  py: '15px',
                  gap: '8px',
                  fontSize: '16px',
                  lineHeight: '26px',
                  fontWeight: 500,
                  color: theme.palette.custom.zBlack200,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                  '&.Mui-focusVisible': {
                    backgroundColor: 'transparent',
                  },
                })}>
                <Stack className="flex flex-row items-center gap-2 w-full">
                  {/* Address content */}
                  <Stack className="flex-row flex-1 min-w-0">
                    <Typography
                      sx={(theme) => ({
                        fontSize: '16px',
                        lineHeight: '26px',
                        fontWeight: 500,
                        color: theme.palette.custom.zBlack200,
                      })}>
                      {prediction.description}
                    </Typography>
                  </Stack>
                </Stack>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </div>
  )
}
